﻿import { DataGrid, Fluent, SettingStorage, faIcon, notifySuccess, notifyWarning, stringFormat } from "@serenity-is/corelib";
import { FavoriteViewsMixinTexts } from "../ServerTypes/Texts";

/**
 * A mixin that can be applied to a DataGrid for favorite views functionality
 */
export class FavoriteViewsMixin<TItem> {

    declare private dataGrid: DataGrid<TItem, any>;
    declare private ul: Fluent;

    constructor(options: FavoriteViewsMixinOptions<TItem>) {
        var dg = this.dataGrid = options.grid;

        let viewName: HTMLInputElement;
        const saveButtonClick = async () => {
            var name = viewName.value?.trim();
            if (!name) {
                notifyWarning(FavoriteViewsMixinTexts.EmptyNameError);
                return;
            }

            const favorites = await this.getFavorites();
            favorites[name] = (dg as any).getCurrentSettings();
            await this.saveFavorites(favorites);
            notifySuccess(stringFormat(FavoriteViewsMixinTexts.SaveSuccessMessage, name));
            viewName.value = "";
            this.populateFavorites();
        }

        var dropdown = (
            <div class="dropdown favorite-views" style="float: right">
                <button class="btn btn-primary dropdown-toggle" type="button" id={`${dg.idPrefix}Favorites`} data-toggle="dropdown" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="true">
                    <i class={faIcon("star")}></i> {FavoriteViewsMixinTexts.FavoriteViews}
                    <span class="caret"></span>
                </button>
                <ul class="dropdown-menu" aria-labelledby="dropdownMenu1" ref={el => this.ul = Fluent(el)}>
                    <li class="save p-2">
                        <div><input class="my-2 w-100" type="text" ref={el => viewName = el} placeholder={FavoriteViewsMixinTexts.SaveView} /></div>
                        <div><button class="btn btn-primary btn-sm save-button" onClick={saveButtonClick}>
                            <i class={faIcon("save")}></i>{" "}{FavoriteViewsMixinTexts.SaveButton}</button></div>
                    </li>
                </ul>
            </div>
        ) as HTMLElement;
        dg.domNode.querySelector(".grid-title")?.append(dropdown);

        Fluent.on(dropdown, "shown.bs.dropdown", e => {
            dropdown.querySelector<HTMLInputElement>('input[type=text]')?.focus();
        });

        this.ul.on('click', '.delete-favorite', async (e: Event) => {
            var name = Fluent(e.target).closest('li').findFirst('a').text();
            const favorites = await this.getFavorites();
            delete favorites[name];
            await this.saveFavorites(favorites);
            await this.populateFavorites();
            notifyWarning(stringFormat(FavoriteViewsMixinTexts.DeleteSuccessMessage, name));
        });

        this.ul.on('click', 'a', async (e: Event) => {
            var name = Fluent(e.target).text();
            const favorites = await this.getFavorites();
            var settings = favorites[name];
            if (settings) {
                dg["restoreSettingsFrom"](settings);
                dg.refresh();
                notifySuccess(stringFormat(FavoriteViewsMixinTexts.LoadedViewMessage, name));
            }
        });

        this.populateFavorites();
    }

    private async populateFavorites(): Promise<void> {
        var favorites = await this.getFavorites();

        var saveLI = this.ul.findFirst('.save').getNode();
        this.ul.children(":not(.save)").forEach(Fluent.remove);

        var keys = Object.keys(favorites).sort();
        if (keys.length) {
            saveLI.before(<>
                {keys.map(k => <li class="fav px-2 py-1 d-flex">
                    <a href="javascript:;">
                        {k}
                    </a>
                    <i class={[faIcon("trash"), "delete-favorite", "ms-auto"]} title={FavoriteViewsMixinTexts.DeleteButtonHint}></i>
                </li>)
                }
                <li role="separator" class="divider my-1 border-top"></li>
            </>)
        }
    }

    private async saveFavorites(favorites: object): Promise<void> {
        var storage = (this.dataGrid as any).getPersistanceStorage() as SettingStorage;
        var key = "Views:" + (this.dataGrid as any).getPersistanceKey();
        return Promise.resolve(storage.setItem(key, JSON.stringify(favorites)));
    }

    private async getFavorites(): Promise<object> {
        var storage = (this.dataGrid as any).getPersistanceStorage() as SettingStorage;
        var key = "Views:" + (this.dataGrid as any).getPersistanceKey();
        var json = (await storage.getItem(key)) || "{}"
        return JSON.parse(json) || {};
    }
}

export interface FavoriteViewsMixinOptions<TItem> {
    // data grid object
    grid: DataGrid<TItem, any>;
}